import type { ButtonHTMLAttributes, CSSProperties } from "react";
import style from "./style.module.css";
import Loading from "../loading";

// The rest props are spread onto the <button>, so every native button
// attribute is accepted alongside the presentation props below.
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  theme?: string;
  /** Theme used instead of `theme` while disabled. */
  disabledTheme?: string | null;
  isWide?: boolean;
  isExtraWide?: boolean;
  /** Background and border colour; only applied when enabled. */
  color?: string;
  isFullWidth?: boolean;
  isHoverable?: boolean;
  isLoading?: boolean;
  "data-testid"?: string;
}

const Button = ({
  children,
  theme = "blue",
  disabledTheme = null,
  className,
  onClick,
  disabled,
  isWide,
  isExtraWide,
  color,
  isFullWidth,
  isHoverable,
  style: customStyle = {},
  isLoading,
  "data-testid": dataTestId,
  ...props
}: ButtonProps) => {
  let dynamicStyle: CSSProperties = {
    justifyContent: Array.isArray(children) ? "space-between" : "center",
    ...customStyle
  };

  if (!disabled) {
    dynamicStyle = {
      ...dynamicStyle,
      backgroundColor: color,
      border: `solid 1px ${color}`
    };
  }

  const dynamicTheme = (disabled ? disabledTheme : theme) ?? "";

  return (
    <button
      {...props}
      onClick={onClick}
      className={`${style.button} ${style[dynamicTheme]} ${className}`}
      style={dynamicStyle}
      disabled={disabled}
      data-is-wide={isWide}
      data-is-extra-wide={isExtraWide}
      data-disabled={disabled}
      data-is-full-width={isFullWidth}
      data-is-hoverable={isHoverable}
      data-testid={dataTestId || "Button"}
    >
      {isLoading ? (
        <Loading hexColor={disabled ? "#000000" : "#D1D4D6"} />
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
